/**
 * @prettier
 */

// React Packages
import { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

// Helpers
import { getCompanyName, isInIframe } from "../../services/shared/helpers";

// Components
import Header from "../../components/Header";
import RedirectSection from "./components/RedirectSection";

// Services
import { runSubmissionCompletionSideEffects } from "../../services/shared/submissionCompletion";

// Images
import SuccessSvg from "../../assets/animations/success_complete.svg";

// Alt Text
import { imageAlt } from "../../config/accessabilityRules";

const SUCCESS_SCREEN_OPTIONS = {
    basic: "basic",
    mobile: "mobile",
    default: "default",
};

const SuccessPage = ({ branding, isHandoff }) => {
    const [successScreen, setSuccessScreen] = useState("default");
    const primaryFocusRef = useRef();
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [showFirstContainer, setShowFirstContainer] = useState(true);

    const getSuccessMessage = useCallback(() => {
        const isEmbeddedInIframe = isInIframe();

        if (isEmbeddedInIframe && !isHandoff) {
            return SUCCESS_SCREEN_OPTIONS.basic;
        } else if (isHandoff) {
            return SUCCESS_SCREEN_OPTIONS.mobile;
        } else {
            return SUCCESS_SCREEN_OPTIONS.default;
        }
    }, [isHandoff]);

    useEffect(() => {
        setSuccessScreen(getSuccessMessage());
        runSubmissionCompletionSideEffects();
        if (primaryFocusRef && primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }

        document.title = t("idpal_doc_title_success");
    }, [getSuccessMessage, t]);

    useEffect(() => {
        setVisible(true);
        const fadeOutTimer = setTimeout(() => {
            setVisible(false);
            setShowFirstContainer(false);
        }, 1000);

        const switchTimer = setTimeout(() => {
            setVisible(true);
        }, 4500);

        return () => {
            clearTimeout(fadeOutTimer);
            clearTimeout(switchTimer);
        };
    }, []);

    return (
        <div className="root-layout-container success-page">
            <Header />
            <div className="">
                {successScreen === SUCCESS_SCREEN_OPTIONS.basic && (
                    <div>
                        <object
                            type="image/svg+xml"
                            data={SuccessSvg}
                            height={"300px"}
                            width={"100%"}
                            aria-label={imageAlt.success}
                        />
                        <h1
                            className="primary-text"
                            style={{ textAlign: "center" }}
                            ref={primaryFocusRef}
                            tabIndex={-1}
                        >
                            {t("idpal_success")}
                        </h1>
                        <RedirectSection />
                    </div>
                )}

                {(successScreen === SUCCESS_SCREEN_OPTIONS.mobile ||
                    successScreen === SUCCESS_SCREEN_OPTIONS.default) && (
                    <>
                        <object
                            type="image/svg+xml"
                            data={SuccessSvg}
                            height={"300px"}
                            width={"100%"}
                            aria-label={imageAlt.success}
                        />
                        <section
                            className={`fade-container`}
                            style={{
                                opacity: visible && showFirstContainer ? 1 : 0,
                            }}
                        >
                            <h1
                                className="primary-text"
                                ref={primaryFocusRef}
                                style={{ textAlign: "center" }}
                                tabIndex={-1}
                            >
                                {t("idpal_upload_complete")}
                            </h1>
                        </section>

                        <section
                            className="fade-container"
                            style={{
                                opacity: visible && !showFirstContainer ? 1 : 0,
                            }}
                        >
                            <h1
                                className="primary-text"
                                ref={primaryFocusRef}
                                tabIndex={-1}
                            >
                                {t("idpal_your_submission_being_processed", {
                                    company: getCompanyName(branding.name),
                                })}
                            </h1>
                            <p className="secondary-text">
                                {t("idpal_if_you_have_any_queries")}
                                <a
                                    className="e-address"
                                    href={`mailto:${branding.costumer_support_email}`}
                                >
                                    {branding.costumer_support_email}
                                </a>
                            </p>
                            <RedirectSection />
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        isHandoff: state.submissionState.isHandoff,
        branding: state.config.profile.data.company_branding,
    };
}

export default connect(mapStateToProps)(SuccessPage);
